import { UUID } from "crypto";
import axios from "axios";

import useUserContext from "../Authorization/useUserContext";
import { buildHeaders } from "../../api/utils";
import { callDeleteStudyFileAPI, uploadDocumentToS3 } from "../../api/dataSources";

const API_URI = import.meta.env.VITE_APP_API_URI;

/**
 * Interface for UPLOAD /api/upload/file routes
 */
export default function useStudyFileStore() {
  const user = useUserContext();

  const registerFile = async (deepsense_file_path: string, original_name: string) => {
    const response = await axios.post<UploadFileResponse>(
      `${API_URI}/upload/file`,
      { deepsense_file_path, original_name },
      {
        headers: buildHeaders(user),
      },
    );

    if (axios.isAxiosError(response)) {
      throw new Error("Failed to register filename with storage API");
    }

    return response.data;
  };

  const generateUploadUrl = async (deepsense_file_guid: UUID) => {
    const response = await axios.put<PresignedUrlResponse>(`${API_URI}/upload/file/${deepsense_file_guid}`, undefined, {
      headers: buildHeaders(user),
    });

    if (axios.isAxiosError(response)) {
      throw new Error("Failed to generate presigned URL for upload");
    }

    return response.data;
  };

  const generateDownloadUrl = async (deepsense_file_guid: UUID) => {
    const response = await axios.get<PresignedUrlResponse>(`${API_URI}/upload/file/${deepsense_file_guid}`, {
      headers: buildHeaders(user),
    });

    if (axios.isAxiosError(response)) {
      throw new Error("Failed to generate presigned URL for download");
    }

    return response.data;
  };

  const downloadFile = async (deepsense_file_guid: UUID, file_name: string) => {
    const s3 = await generateDownloadUrl(deepsense_file_guid);

    const link = document.createElement("a");
    link.href = s3.presigned_url;
    link.target = "_blank";
    link.click();
  };

  const deleteFile = async (audio_file_guid: UUID) => {
    const response = await callDeleteStudyFileAPI(user, audio_file_guid);
  };

  return {
    /** Generate a UUID for a filename that can be used to create S3 upload URLs  */
    registerFile,

    /** Generate a presigned URL from a UUID for uploading a file asset */
    generateUploadUrl,

    /** Generate a presigned URL from a UUID for downloading a file asset */
    generateDownloadUrl,

    // Proxying this here since its a file operation but is defined elsewhere, may move/change later
    uploadFile: uploadDocumentToS3,

    /** Trigger a file download from a file UUID */
    downloadFile,

    deleteFile,
  };
}

type UploadFileResponse = {
  deepsense_file_guid: UUID;
};

type PresignedUrlResponse = {
  presigned_url: string;
};
